import React, { useMemo } from "react";
import { AiOutlineArrowRight } from "react-icons/ai";
import useFetch from "../../../../../hooks/useFetch";
import InitialData from "../../../../globals/InitialData";
import { Table } from "antd";
import useDebounce from "../../../../../hooks/useDebounce";
import { isNumber } from "../../../../../helpers/number";
import { numberRound, returnEuroString } from "../../../../../helpers/Utils";
import { capitalizeFirstLetter } from "../../../../../utils/string";
import { have_product_reference } from "../../../../../helpers/OrdersFunctionsGlobal";

const rebuildObject = (obj) => {
  Object.keys(obj).filter((val) => {
    if (
      [
        "updated_at",
        "created_at",
        "id",
        "order_id",
        "shipment_id",
        "id_order",
        "id_cart",
        "id_category",
        "category_id",
        "id_product",
        "id_manufacturer",
        "user_title",
        "customer_id",
        "invoice_address_id",
        "shipping_address_id",
        "order_cart_rule_id",
        "order_shop_id",
        "row_categories",
        "document_plan_id",
        "service_order_row_id",
        "assigned_bills",
        "created",
        "user_id",
        "parent_id",
        "total_products",
        "order_type",
        "order_active"
      ].includes(val)
    ) {
      delete obj[val];
    }
    if (val.includes("unit_price")) {
      delete obj[val];
    }
    if (val.includes("_tax_excl")) {
      delete obj[val];
    }

    if (val.includes("_tax_incl")) {
      obj[val.replace("_tax_incl", "")] = obj[val];
      delete obj[val];
    }
    if (val.includes("total_products_wt")) {
      obj[val.replace("_wt", "")] = obj[val];
      delete obj[val];
    }
  });
  return obj;
};

const moneyKeys = [
  "total",
  "total_shipping",
  "total_price",
  "total_discounts",
  "price",
  "discount",
  "discount_amount",
  "product_price",
  "service_price",
  "administrative_fee",
];

const rowPriceKeys = ["product_price", "service_price", "administrative_fee"];

const booleanKeys = [
  "manual",
  "cod",
  "leasing_cod",
  "order_active",
  "not_ready",
];

const numericDifference = (key, values) => {
  if (booleanKeys.includes(key)) return null;
  if (!Array.isArray(values)) return null;

  const [oldValue, newValue] = values;
  const isFilled = (val) => val !== null && val !== undefined && val !== "";
  if (typeof oldValue === "boolean" || typeof newValue === "boolean") return null;
  if (!isFilled(oldValue) || !isFilled(newValue)) return null;
  if (!isNumber(oldValue) || !isNumber(newValue)) return null;

  const difference = +newValue - +oldValue;
  return difference ? difference : null;
};

const differenceLabel = (key, difference) => {
  const value = moneyKeys.includes(key)
    ? returnEuroString(difference)
    : numberRound(difference);

  return `${difference > 0 ? "+" : ""}${value}`;
};

const keyName = (key) => {
  if (key == "total_shipping") return "Delivery price";
  return capitalizeFirstLetter(
    key
      .split("_")
      .filter((str) => str !== "id")
      .join(" ")
  );
};

const valueSetter = (key, val) => {
  // console.log({key, val})
  if (
    ["manual", "cod", "leasing_cod", "order_active", "not_ready"].includes(key)
  ) {
    return !!val ? "True" : "False";
  }
  if (["product_reference"].includes(key))
    return have_product_reference(val) ? val : "Service";

  if (["discount_code"].includes(key)) return val;

  if (["amount", "quantity"].includes(key)) return numberRound(val);
  if (moneyKeys.includes(key)) return returnEuroString(val);
  if (isNumber(val)) return numberRound(val);

  if (val == 'created_through') return "Created through"
  if (val == 'bulk_action') return "Bulk action"
  return val;
};

const keys = {
  Contract: { title: "Warranty", mode: "simple" },
  OrderRow: { title: "Product", mode: "simple" },
  ShipmentAdditionalService: { title: "Shipment Additional Service", mode: "simple" },
  ShipmentOrderRow: { title: "Shipment product", mode: "simple" },
  WaitingPayment: { title: "Waiting payment", mode: "simple" },
  OrderCartRule: { title: "Discount", mode: "simple" },
  OrderInvoice: { title: "Invoice", mode: "simple" },
  Supplying: { title: "Supply", mode: "simple" },

  //
  status_changed: { title: "Status changed", mode: "block" },
  supply_changed: { title: "Supply changed" },
  delivery_type_changed: { title: "Delivery type changed" },
  generated_invoice: { title: "Generated invoice", mode: "block" },
  warranty_created: { title: "Warranty created", mode: "block" },
  shipping_costs: { title: "Shipping costs" },
  payment_type_changed: { title: "Payment type" },
};

export default function OrderActivities({ order_id }) {
  const { value, loading, error, reFetch, setReFetch } = useFetch(
    `/api/v1/order_activities/${order_id}`
  );

  useDebounce(
    () => {
      if (loading) return;
      console.log("---- Refetching Order Activities ----");
      setReFetch();
    },
    10000,
    [loading, reFetch]
  );

  const rebuildValue = useMemo(() => {
    if (!value) return [];
    return value
      .map((record, index) => {
        const recordKey =
          record.id ||
          `${record.created_at || "no-date"}-${record.event || "no-event"}-${index}`;

        return {
          ...record,
          key: recordKey,
          records: Object.entries(rebuildObject(record.records)),
        };
      })
      .filter((record) => record.records.length > 0);
  }, [value]);

  if (error) {
    return <InitialData error={error} loading={loading} />;
  }

  const columns = [
    {
      title: "Type of action",
      dataIndex: "event",
      sorter: (a, b) => a.event - b.event,
      render: (_, values) => {
        const item_type =
          values.item_title || keys[values.item_type]?.title || values.item_type;
        let event = values.event + "d";

        if (
          ["OrderRow", "OrderCartRule"].includes(values.item_type) &&
          event == "created"
        ) {
          event = "added";
        }
        if (event == "destroyd") {
          event = "deleted";
        }
        return (
          <span className="text-xs">
            <div>
              {item_type} {event}
            </div>
            {!!values?.reference && (
              <div className="font-medium">{values.reference}</div>
            )}
          </span>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "created_at",
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (created_at) => {
        return (
          <div className="text-xs">
            <div>{created_at.substring(0, 10)}</div>
            <div className="text-silver">{created_at.substring(11, 16)}</div>
          </div>
        );
      },
    },
    {
      title: "User",
      dataIndex: "user_name",
      render: (value) => <span className="text-xs">{value}</span>,
    },
    {
      title: "Action",
      dataIndex: "old_record",
      render: (_, record) => <MainRecordPreview record={record} />,
    },
  ];

  return (
    <div className="pt-3 bg-silver">
      <Table
        // size="middle"
        rowClassName={() => "bg-silver"}
        size="small"
        columns={columns}
        dataSource={rebuildValue}
        rowKey="key"
        pagination={false}
        loading={loading && reFetch == 1}
      />
    </div>
  );
}

const MainRecordPreview = ({ record }) => {
  const hasDiscountInfo = record.records.some(
    ([key, value]) =>
      (key === "discount_amount" || key.endsWith("_discount")) &&
      Array.isArray(value) &&
      value.some((d) => d)
  );
  const extraFiltered = record.records.filter((s) => {
    if (!s) return false;
    if (!s[1]) return false;
    if (!Array.isArray(s[1])) return false;
    return s[1].some((d) => d);
  });
  return (
    <div className="text-xs">
      {extraFiltered.map(([key, value], idx) => {
        const difference =
          hasDiscountInfo && rowPriceKeys.includes(key)
            ? null
            : numericDifference(key, value);

        return (
          <div key={`records_${record.key}_${key}_${idx}`}>
            {idx > 0 && <hr />}
            <div className="font-medium">{keyName(key)}</div>
            {value.map((d, idx) => (
              <React.Fragment key={idx}>
                {idx == 0 && d && (
                  <RecordPreview mode={key}>
                    {valueSetter(key, d)}
                  </RecordPreview>
                )}
                {idx == 1 && d && (
                  <RecordPreview itsNew={true} mode={key}>
                    {valueSetter(key, d)}
                  </RecordPreview>
                )}
              </React.Fragment>
            ))}
            {difference !== null && (
              <div style={{ color: difference < 0 ? "#690B0B" : "#115300" }}>
                {differenceLabel(key, difference)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const RecordPreview = ({ children, mode, itsNew }) => {
  if (mode == "block") {
    return <Block itsNew={itsNew}>{children}</Block>;
  }

  if (["status_id", "carrier_id", "invoice_number"].includes(mode)) {
    return <Block itsNew={itsNew}>{children}</Block>;
  }
  if (itsNew) {
    return (
      <div
        style={{
          color: mode == "simple" ? "#000" : "#115300",
        }}
      >
        {children}
      </div>
    );
  }
  return (
    <del>
      <div
        style={{
          color: "#690B0B",
        }}
      >
        {children}
      </div>
    </del>
  );
};

const Block = ({ children, itsNew }) => (
  <div className={`d-flex align-items-center ${itsNew ? "" : "mb-2"}`}>
    <div
      style={{
        border: "1px solid #DEE2E6",
        borderRadius: 2,
        padding: "4px 5px",
      }}
    >
      {children}
    </div>
    {!itsNew && <AiOutlineArrowRight className="ml-2" color="#979797" />}
  </div>
);
